import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { CheckCircle2, Circle, ChevronUp, X, Sparkles } from 'lucide-react';
import { db } from '../firebase-config';
import { getManagedDocuments } from '../services/documentManagerService';
import { getSearchHistory } from '../services/legalResearchService';
import { getFirmInvites } from '../services/userService';
import { getTeamMembers } from '../services/teamService';

// Product Onboarding V1 — action-based dashboard checklist.
//
// Completion for every item is derived from existing production data (never
// a new "completed" boolean) and the only new persistence this introduces is
// two extra fields — onboardingDismissed / onboardingMinimized — merged onto
// the SAME notificationPreferences/{uid} document PersonalNotificationSettings
// already owns. That document is already strictly self-uid-scoped in
// firestore.rules, so this requires no security-rule change.

const persistOnboardingState = async (fields) => {
  const uid = getAuth().currentUser?.uid;
  if (!uid) return;
  try {
    await setDoc(doc(db, 'notificationPreferences', uid), fields, { merge: true });
  } catch (e) {
    // Non-critical UI preference — a failed write just means the checklist
    // reappears next load, which is safe.
  }
};

const DashboardOnboardingChecklist = ({
  userId,
  firmId,
  userRole,
  userPermissions,
  planFeatures,
  recentCases,
  upcomingTasks,
  setActiveTab,
  onOpenNewCase,
  activeTab,
  onNavigateToPersonalNotificationSettings,
}) => {
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [completion, setCompletion] = useState(null);

  // ONB-07: Dashboard (and this checklist inside it) never unmounts when the
  // user navigates to another section — App.js keeps it alive with
  // display:none so its own stats don't refetch on every tab switch. That
  // means completion signals for actions taken elsewhere (uploading a
  // document, running Ratio research, saving a reminder number) would never
  // be re-read without this: re-running the same fetch whenever `activeTab`
  // transitions back to 'dashboard' re-evaluates persisted state exactly
  // like a fresh mount would, using the same canonical reads — no polling,
  // no timers, no new completion flags, no full reload.
  useEffect(() => {
    if (activeTab !== 'dashboard') return undefined;

    let cancelled = false;
    const uid = getAuth().currentUser?.uid;
    if (!uid) { setLoading(false); return undefined; }

    const isAdminWithTeam = userRole === 'admin' && !!planFeatures?.teamHub;

    (async () => {
      const [userSnap, prefsSnap, docsRes, researchRes, teamRes] = await Promise.all([
        getDoc(doc(db, 'users', uid)),
        getDoc(doc(db, 'notificationPreferences', uid)),
        getManagedDocuments(userId, {}, firmId).catch(() => ({ success: false, documents: [] })),
        getSearchHistory(userId).catch(() => ({ success: false, items: [] })),
        isAdminWithTeam
          ? Promise.all([
              getFirmInvites(firmId).catch(() => ({ success: false, invites: [] })),
              getTeamMembers(userId, firmId).catch(() => ({ success: false, members: [] })),
            ])
          : Promise.resolve(null),
      ]);

      if (cancelled) return;

      const userData = userSnap.exists() ? userSnap.data() : {};
      const prefsData = prefsSnap.exists() ? prefsSnap.data() : {};

      let teamDone = false;
      if (teamRes) {
        const [invitesRes, membersRes] = teamRes;
        teamDone = (invitesRes.success && invitesRes.invites.length > 0) ||
          (membersRes.success && membersRes.members.length > 1);
      }

      setDismissed(!!prefsData.onboardingDismissed);
      setMinimized(!!prefsData.onboardingMinimized);
      setCompletion({
        matter: (recentCases || []).length > 0,
        // Only a court date or deadline satisfies this item — an ordinary
        // task must not. upcomingTasks is already the firm/user's full,
        // tenant-scoped task set (see enhancedTaskService.getEnhancedTasks
        // call in App.js — no eventType/date filter is applied there), so
        // it already contains whatever eligible records exist; no separate
        // fetch is needed.
        event: (upcomingTasks || []).some(t => t.eventType === 'court_date' || t.eventType === 'deadline'),
        document: !!(docsRes.success && docsRes.documents.length > 0),
        research: !!(researchRes.success && researchRes.items.length > 0),
        reminder: !!(userData.whatsappNumber || userData.phone),
        whatsapp: !!userData.whatsappVerifiedNumber,
        team: teamDone,
      });
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [activeTab, userId, firmId, userRole, planFeatures, recentCases, upcomingTasks]);

  if (loading || !completion || dismissed) return null;

  const items = [
    {
      key: 'matter',
      title: 'Create your first matter',
      desc: 'Keep the case, parties, documents, deadlines and activity together.',
      cta: 'Create Matter',
      visible: !!userPermissions?.canManageCases,
      onClick: () => (onOpenNewCase ? onOpenNewCase() : setActiveTab && setActiveTab('cases')),
    },
    {
      key: 'event',
      title: 'Add a court date or deadline',
      desc: 'Track the event and let FirmAssist remind the responsible lawyer.',
      cta: 'Add Event',
      visible: !!userPermissions?.canManageTasks,
      onClick: () => setActiveTab && setActiveTab('tasks'),
    },
    {
      key: 'document',
      title: 'Upload your first document',
      desc: 'Keep matter documents organised and accessible from the case workspace.',
      cta: 'Upload Document',
      visible: !!userPermissions?.canManageDocuments,
      onClick: () => setActiveTab && setActiveTab('documents'),
    },
    {
      key: 'research',
      title: 'Try Ratio Legal Research',
      desc: 'Research Ghanaian law with authorities and source-linked results.',
      cta: 'Try Ratio',
      visible: !!userPermissions?.canUseResearch,
      onClick: () => setActiveTab && setActiveTab('legal-research'),
    },
    {
      key: 'reminder',
      title: 'Set up reminders',
      desc: 'Choose where FirmAssist should send your personal court and deadline alerts.',
      cta: 'Set Up',
      visible: true,
      onClick: () => (onNavigateToPersonalNotificationSettings
        ? onNavigateToPersonalNotificationSettings('reminder')
        : setActiveTab && setActiveTab('admin')),
    },
    {
      key: 'whatsapp',
      title: 'Connect WhatsApp',
      desc: 'Use FirmAssist from WhatsApp for research and authorised matter lookups.',
      cta: 'Connect',
      visible: true,
      onClick: () => (onNavigateToPersonalNotificationSettings
        ? onNavigateToPersonalNotificationSettings('whatsapp')
        : setActiveTab && setActiveTab('admin')),
    },
    {
      key: 'team',
      title: 'Invite your team',
      desc: 'Add lawyers and staff to your FirmAssist workspace.',
      cta: 'Invite',
      visible: userRole === 'admin' && !!planFeatures?.teamHub,
      onClick: () => setActiveTab && setActiveTab('team'),
    },
  ].filter(item => item.visible);

  const total = items.length;
  const completedCount = items.filter(item => completion[item.key]).length;

  if (total === 0) return null;

  // Completion must never, by itself, hide this component — only an
  // explicit Dismiss action may do that (see handleDismiss below). Reaching
  // 100% is derived live from canonical data on every revisit (ONB-07), so
  // it can also become false again later (e.g. a WhatsApp disconnect) —
  // this is a plain boolean check, not persisted state.
  const isComplete = completedCount === total;

  const handleDismiss = () => {
    setDismissed(true);
    persistOnboardingState({ onboardingDismissed: true, onboardingMinimized: false });
  };

  const cardBg = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(212,168,83,0.25)', borderRadius: '16px' };

  if (isComplete) {
    return (
      <div style={{ ...cardBg, padding: '1.1rem 1.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <CheckCircle2 size={22} style={{ color: '#10b981', flexShrink: 0 }} />
          <div>
            <div style={{ color: '#e8eaed', fontWeight: 600, fontSize: '0.95rem' }}>Setup complete</div>
            <div style={{ color: '#a8adb7', fontSize: '0.82rem', marginTop: '0.15rem' }}>
              You're ready to use FirmAssist. {completedCount} of {total} completed.
            </div>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          style={{
            flexShrink: 0, background: 'rgba(212,168,83,0.12)', border: '1px solid rgba(212,168,83,0.3)',
            borderRadius: '8px', padding: '0.5rem 1rem', color: '#d4a853', cursor: 'pointer',
            fontSize: '0.82rem', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap',
          }}
        >
          Dismiss
        </button>
      </div>
    );
  }

  if (minimized) {
    return (
      <div style={{ ...cardBg, padding: '0.9rem 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <Sparkles size={18} style={{ color: '#d4a853' }} />
          <span style={{ color: '#e8eaed', fontWeight: 600, fontSize: '0.9rem' }}>Get started with FirmAssist</span>
          <span style={{ color: '#a8adb7', fontSize: '0.85rem' }}>{completedCount} of {total} complete</span>
        </div>
        <button
          onClick={() => { setMinimized(false); persistOnboardingState({ onboardingMinimized: false }); }}
          className="btn-ghost"
          style={{ fontSize: '0.85rem' }}
        >
          Resume
        </button>
      </div>
    );
  }

  return (
    <div style={{ ...cardBg, padding: '1.5rem', marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
        <div>
          <h3 style={{ color: '#e8eaed', margin: 0, marginBottom: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Sparkles size={20} style={{ color: '#d4a853' }} /> Get started with FirmAssist
          </h3>
          <p style={{ color: '#a8adb7', fontSize: '0.85rem', margin: 0 }}>{completedCount} of {total} complete</p>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          <button
            onClick={() => { setMinimized(true); persistOnboardingState({ onboardingMinimized: true }); }}
            title="Minimize — resume later"
            style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '0.25rem' }}
          >
            <ChevronUp size={18} />
          </button>
          <button
            onClick={handleDismiss}
            title="Dismiss"
            style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '0.25rem' }}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gap: '0.6rem' }}>
        {items.map(item => {
          const done = completion[item.key];
          return (
            <div
              key={item.key}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
                padding: '0.85rem 1rem', borderRadius: '10px',
                background: done ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.03)',
                border: '1px solid ' + (done ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.06)'),
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flex: 1 }}>
                {done
                  ? <CheckCircle2 size={20} style={{ color: '#10b981', flexShrink: 0, marginTop: '0.1rem' }} />
                  : <Circle size={20} style={{ color: '#6b7280', flexShrink: 0, marginTop: '0.1rem' }} />}
                <div>
                  <div style={{ color: '#e8eaed', fontWeight: 600, fontSize: '0.92rem' }}>{item.title}</div>
                  <div style={{ color: '#a8adb7', fontSize: '0.82rem', marginTop: '0.15rem' }}>{item.desc}</div>
                </div>
              </div>
              {!done && (
                <button
                  onClick={item.onClick}
                  style={{
                    flexShrink: 0, background: 'rgba(212,168,83,0.12)', border: '1px solid rgba(212,168,83,0.3)',
                    borderRadius: '8px', padding: '0.5rem 1rem', color: '#d4a853', cursor: 'pointer',
                    fontSize: '0.82rem', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap',
                  }}
                >
                  {item.cta}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardOnboardingChecklist;
